import { Navigate, Outlet, useLocation } from 'react-router';
import { LoadingScreen } from '@/components/common';
import { ROUTES } from '@/constants';
import { useAppSelector } from '@/store/hooks';

/**
 * Keeps signed-out visitors out of the app.
 *
 * The `isRestoring` check is the part that is easy to miss: on a reload the
 * access token is gone from memory and the refresh is still in flight, so
 * redirecting immediately would bounce a perfectly valid session to the login
 * screen. Waiting costs a spinner for a few hundred milliseconds.
 *
 * This is a convenience, not a security boundary. The server rejects any
 * request without a valid token regardless of what the client renders.
 */
export function RequireAuth() {
  const { isAuthenticated, isRestoring } = useAppSelector((state) => state.auth);
  const location = useLocation();

  if (isRestoring) return <LoadingScreen full label="Restoring your session" />;

  if (!isAuthenticated) {
    // `state` carries where they were going, so signing in returns them there
    // instead of dumping everyone on the dashboard.
    return <Navigate to={ROUTES.login} replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}

/** The mirror image: keeps a signed-in user off the login page. */
export function RequireAnonymous() {
  const { isAuthenticated, isRestoring } = useAppSelector((state) => state.auth);

  if (isRestoring) return <LoadingScreen full label="Loading" />;
  if (isAuthenticated) return <Navigate to={ROUTES.customers} replace />;

  return <Outlet />;
}
